import React, { useState } from 'react';
import { X, Calendar, User, Phone, Mail, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { projects } from '../mock';
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
const API = process.env.REACT_APP_BACKEND_URL;

const BookingModal = ({ isOpen, onClose, selectedProject }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '+91 ',
    email: '',
    project: '',
    preferredDate: '',
    message: '',
    consent: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [errors, setErrors] = useState({});
  React.useEffect(() => {
  if (typeof selectedProject === 'string' && isOpen) {
    setFormData(prev => ({
      ...prev,
      project: selectedProject
    }));
  }
}, [selectedProject, isOpen]);

 const capitalizeName = (value) =>
    value
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    else if (!/^[A-Za-z ]+$/.test(formData.name)) newErrors.name = 'Name can contain only letters and spaces';

    // Phone validation
    if (!formData.phone || !/^\+91 \d{10}$/.test(formData.phone)) {
      newErrors.phone = 'Phone must be +91 followed by 10 digits';
    }

    // Email validation
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Enter a valid email';
    }

    // Project validation
    if (!formData.project) newErrors.project = 'Please select a project';

    // Date validation (max 3 months)
    const maxDate = new Date();
    maxDate.setMonth(maxDate.getMonth() + 3);
    const selectedDate = new Date(formData.preferredDate);
    if (!formData.preferredDate) newErrors.preferredDate = 'Please select a date';
    else if (selectedDate > maxDate) newErrors.preferredDate = 'Date must be within next 3 months';

    // Consent validation
    if (!formData.consent) newErrors.consent = 'You must agree to the terms';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
  const { name, value, checked } = e.target;

  if (name === 'name') {
    const lettersOnly = value.replace(/[^a-zA-Z ]/g, '');
    setFormData(prev => ({ ...prev, name: capitalizeName(lettersOnly) }));
  } 
  else if (name === 'phone') {
  let digits = value.replace(/\D/g, '');

  // Remove leading 91 if user types it
  if (digits.startsWith('91')) {
    digits = digits.slice(2);
  }

  // Limit to 10 digits
  digits = digits.slice(0, 10);

  // Always format as "+91 1234567890"
  const formattedPhone = '+91 ' + digits;

  setFormData(prev => ({ ...prev, phone: formattedPhone }));
}

  else if (name === 'consent') {
    setFormData(prev => ({ ...prev, consent: checked }));
  } 
  else {
    // Handle all other fields like email, message, project
    setFormData(prev => ({ ...prev, [name]: value }));
  }

  if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
};



  const handleSubmit = async (e) => {
  e.preventDefault();
  setIsSubmitting(true);
  setSubmitStatus(null);

  // Validate form
  const isValid = validateForm();
  if (!isValid) {
    setIsSubmitting(false); // reset
    return;
  }

  // --- Save to Firebase ---
  try {
    await addDoc(collection(db, "siteVisitBookings"), {
      ...formData,
      source: "Website Booking Modal",
      status: "new",
      createdAt: serverTimestamp(),
    });
    console.log("✅ Booking saved to Firebase:", formData);
  } catch (firebaseError) {
    console.error("❌ Firebase Booking Error:", firebaseError);
    setSubmitStatus("error");
    setIsSubmitting(false);
    return;
  }
  console.log("🔥 API URL:", API);


  // --- Send email ---
  try {
    const response = await fetch(`${API}/send-booking-email`, {
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(formData),
});


    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Email backend error: ${errorText}`);
    }

    console.log("✅ Email sent successfully");
    setSubmitStatus("success");

    // Reset form
    setTimeout(() => {
      setFormData({
        name: "",
        phone: "+91 ",
        email: "",
        project: "",
        preferredDate: "",
        message: "",
        consent: false,
      });
      setSubmitStatus(null);
      onClose();
    }, 2000);

  } catch (emailError) {
    console.error("❌ Email Sending Error:", emailError);
    setSubmitStatus("error");
  } finally {
    setIsSubmitting(false);
  }
};




  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4 overflow-y-auto">
      <div className="bg-white rounded-lg w-full max-w-2xl my-8 shadow-2xl animate-in fade-in zoom-in duration-300 overflow-y-auto max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-brand-dark">Book Site Visit</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors" aria-label="Close">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 sm:px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name" className="flex items-center gap-2">
                <User size={16} className="text-brand-gold" />
                Full Name <span className="text-red-500">*</span>
              </Label>
              <Input
  id="name"
  name="name"
  type="text"
  value={formData.name}
  onChange={handleChange}
  onKeyPress={(e) => {
    const regex = /^[a-zA-Z ]$/; // only letters and space allowed
    if (!regex.test(e.key)) {
      e.preventDefault(); // prevent typing invalid character
    }
  }}
  placeholder="Enter your name"
  className="w-full border border-gray-300 focus:ring-2 focus:ring-brand-gold"
/>

              {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label htmlFor="phone" className="flex items-center gap-2">
                <Phone size={16} className="text-brand-gold" />
                Phone Number <span className="text-red-500">*</span>
              </Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+91 XXXXX XXXXX"
                className="w-full border border-gray-300 focus:ring-2 focus:ring-brand-gold"
              />
              {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail size={16} className="text-brand-gold" />
                Email Address <span className="text-red-500">*</span>
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="your@email.com"
                className="w-full border border-gray-300 focus:ring-2 focus:ring-brand-gold"
              />
              {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
            </div>

            {/* Project */}
<div className="space-y-2">
  <Label className="flex items-center gap-2">
    <Calendar size={16} className="text-brand-gold" />
    Project Interest <span className="text-red-500">*</span>
  </Label>

  {selectedProject ? (
  <div
    className="
      w-full h-10 px-3
      flex items-center
      rounded-md
      border border-gray-300
      bg-gray-50
      text-gray-700
    "
  >
    {selectedProject}
  </div>
) : (

    // ✅ HEADER / OTHER PAGES → SHOW DROPDOWN
    <Select
      value={formData.project}
      onValueChange={(value) =>
        setFormData(prev => ({ ...prev, project: value }))
      }
    >
      <SelectTrigger className="w-full border border-gray-300 focus:ring-2 focus:ring-brand-gold">
        <SelectValue placeholder="Select a project" />
      </SelectTrigger>

      <SelectContent className="z-[70]">
        {projects.map(project => (
          <SelectItem key={project.id} value={project.name}>
            {project.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )}

  {errors.project && (
    <p className="text-red-500 text-sm mt-1">{errors.project}</p>
  )}
</div>


            {/* Preferred Date */}
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="preferredDate" className="flex items-center gap-2">
                <Calendar size={16} className="text-brand-gold" />
                Preferred Visit Date <span className="text-red-500">*</span>
              </Label>
              <Input
                id="preferredDate"
                name="preferredDate"
                type="date"
                value={formData.preferredDate}
                onChange={handleChange}
                min={new Date().toISOString().split('T')[0]}
                max={new Date(new Date().setMonth(new Date().getMonth() + 3)).toISOString().split('T')[0]}
                className="w-full border border-gray-300 focus:ring-2 focus:ring-brand-gold"
              />
              {errors.preferredDate && <p className="text-red-500 text-sm mt-1">{errors.preferredDate}</p>}
            </div>

            {/* Message */}
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="message" className="flex items-center gap-2">
                <MessageSquare size={16} className="text-brand-gold" />
                Additional Message
              </Label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell us more about your requirements..."
                rows={4}
                className="w-full border border-gray-300 focus:ring-2 focus:ring-brand-gold"
              />
            </div>

            {/* Consent */}
            <div className="md:col-span-2 flex items-start space-x-2">
              <input
                type="checkbox"
                id="consent"
                name="consent"
                checked={formData.consent}
                onChange={handleChange}
                className="mt-1"
              />
              <label htmlFor="consent" className="text-sm text-gray-600">
                I agree to be contacted by DVV Projects regarding my inquiry and consent to the collection of my information. <span className="text-red-500">*</span>
              </label>
              {errors.consent && <p className="text-red-500 text-sm mt-1">{errors.consent}</p>}
            </div>
          </div>

          {/* Submit Status */}
          {submitStatus === 'success' && (
            <div className="mt-4 p-4 bg-green-50 text-green-800 rounded-lg text-center">
              Your site vaisit request has been submitted successfully!
            </div>
          )}
          {submitStatus === 'error' && (
            <div className="mt-4 p-4 bg-red-50 text-red-800 rounded-lg text-center">
              Something went wrong. Please try again.
            </div>
          )}

          {/* Buttons */}
          <div className="mt-6 flex flex-col md:flex-row gap-4">
            <Button type="button" onClick={onClose} variant="outline" className="w-full md:flex-1">Cancel</Button>
            <Button type="submit" disabled={isSubmitting || !formData.consent} className="w-full md:flex-1 btn-primary">
              {isSubmitting ? 'Submitting...' : 'Submit Request'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BookingModal;
